import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { SubscriptionsBreadcrumb } from '../SubscriptionBreadcrumbs'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { Suspense, useState } from 'react'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { DataTable } from '@/components/ui/DataTable'
import { Button } from '@/components/shadcn/button'
import { CreateWorkspaceForm } from '../Forms/CreateWorkspaceForm'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { isPlatformAdmin, isSuperUser } from '@/util'
import { columns } from './OrganisationWorkspacesTable/organisationWorkspacesTableColumns'
import { ToggleGroup, ToggleGroupItem } from '@/components/shadcn/toggle-group'

export function OrganisationWorkspacesPage() {
  const [activeTab, setActiveTab] = useState<'current' | 'archived'>('current')

  const archiveToggle = (
    <ToggleGroup
      aria-label="Switch between current and archived workspaces"
      className="h-[35px] rounded-md bg-offwhite p-0.5 shadow"
      type="single"
      value={activeTab}
      onValueChange={(value) => {
        if (value) {
          setActiveTab(value as 'current' | 'archived')
        }
      }}
    >
      <ToggleGroupItem className="h-[32px] px-2 text-xs font-medium" value="current">
        Active
      </ToggleGroupItem>
      <ToggleGroupItem className="h-[32px] px-2 text-xs font-medium" value="archived">
        Archived
      </ToggleGroupItem>
    </ToggleGroup>
  )

  const subscriptionBreadcrumbs = <SubscriptionsBreadcrumb title="Workspaces" actions={archiveToggle} />

  if (activeTab === 'archived') {
    return (
      <>
        {subscriptionBreadcrumbs}
        <QueryErrorBoundary errorMessage="Failed to fetch organisation">
          <Suspense fallback={<LoadingComponent className="flex-1" />}>
            <ArchivedWorkspacesPanelContent />
          </Suspense>
        </QueryErrorBoundary>
      </>
    )
  }

  return (
    <>
      {subscriptionBreadcrumbs}
      <QueryErrorBoundary errorMessage="Failed to fetch organisation">
        <Suspense fallback={<LoadingComponent className="flex-1" />}>
          <OrganisationWorkspacesPanelContent />
        </Suspense>
      </QueryErrorBoundary>
    </>
  )
}

function OrganisationWorkspacesPanelContent() {
  const { id: orgId } = useCurrentOrganisation()

  const { getSubscriptionsApiGetOrganisationWorkspacesSummary } = useRestClient()

  const { data: workspacesWithAudit } = getSubscriptionsApiGetOrganisationWorkspacesSummary.useSuspenseQuery({
    query: { orgId },
  })

  const [userRole] = useSessionStorage('roles', 1)
  const isAddWorkspaceAllowed = isPlatformAdmin(userRole) || isSuperUser(userRole)

  return (
    <TablePageContainer>
      <DataTable
        columns={columns}
        data={workspacesWithAudit}
        action={
          <CreateWorkspaceForm
            sheetTitle="New workspace"
            sheetTrigger={
              <Button disabled={!isAddWorkspaceAllowed} size="lg" variant="outline">
                <IconPlusOutline className="mr-2" />
                Create new workspace
              </Button>
            }
          />
        }
      />
    </TablePageContainer>
  )
}

function ArchivedWorkspacesPanelContent() {
  const { id: orgId } = useCurrentOrganisation()
  const { getSubscriptionsApiGetOrganisationWorkspacesSummaryArchived } = useRestClient()

  const { data: archivedWorkspaces } = getSubscriptionsApiGetOrganisationWorkspacesSummaryArchived.useSuspenseQuery({
    query: { orgId },
  })

  return (
    <div>
      <DataTable
        columns={columns}
        data={archivedWorkspaces}
        // action={null}
      />
    </div>
  )
}
